import { useMentiSpring } from '@mentimeter/motion/spring';
import type { PieArcDatum } from 'd3-shape';
import { arc } from 'd3-shape';
import type { PropsWithChildren } from 'react';
import { animated } from '@react-spring/web';
import type { ChoicesData } from '../types';
import { getContrastingTextColor } from '../utils/colors';

interface PieArcProps {
  color: string;
  arcInfo: PieArcDatum<ChoicesData>;
  opacity: number;
  innerRadius?: number;
  outerRadius: number;
  disableAnimations?: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClick?: (() => void) | undefined;
}

export function PieArc({
  color,
  arcInfo,
  opacity,
  innerRadius = 0,
  outerRadius,
  children,
  disableAnimations = false,
  onMouseEnter,
  onMouseLeave,
  onClick,
}: PropsWithChildren<PieArcProps>) {
  const textColor = getContrastingTextColor(color);
  const arcPathGenerator = arc<PieArcDatum<ChoicesData>>()
    .innerRadius(innerRadius)
    .outerRadius(outerRadius)
    .cornerRadius(6)
    .padRadius(180)
    .padAngle(0.016);

  const { startAngle, endAngle } = arcInfo;
  const centroid = arcPathGenerator.centroid(arcInfo);
  const [spring] = useMentiSpring({
    from: {
      angles: [0, 0],
      opacity: 0,
      transform: 'translate(0,0)',
    },
    to: {
      angles: [startAngle, endAngle],
      opacity,
      transform: `translate(${centroid[0].toFixed(10)},${centroid[1].toFixed(10)})`,
    },
    immediate: disableAnimations,
  });

  const animatedAnglesPath = spring.angles.to<string>(
    (startAngle, endAngle) => {
      return (
        arcPathGenerator({
          ...arcInfo,
          startAngle,
          endAngle,
        }) ?? ''
      );
    },
  );

  const angleDifference = Math.abs(endAngle - startAngle);
  const shouldShowValue = angleDifference > 0.4;
  const fontSize = outerRadius * 0.15;

  return (
    <g
      data-name="pie-arc"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      cursor={onClick ? 'pointer' : 'unset'}
    >
      <animated.path
        d={animatedAnglesPath}
        fill={color}
        opacity={spring.opacity}
      />
      {shouldShowValue && (
        <animated.text
          data-testid="pie-arc-score"
          opacity={spring.opacity}
          x={0}
          y={0}
          textAnchor="middle"
          dominantBaseline="middle"
          transform={spring.transform}
          fill={textColor}
          fontSize={fontSize}
          fontWeight={400}
        >
          {children}
        </animated.text>
      )}
    </g>
  );
}
